var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defaults } from '../../defaults';
import { Log } from '../../log';
import { DeferredMerge } from './DeferredUpdate';
import { Timeouts } from './drivers/framework/Timeouts';
const log = Log.channel('CallUpdateManager');
const callProps = [
    'activeMeetingCode',
    'calendarEventId',
    'callExtendedState',
    'callExtendedType',
    'callInListId',
    'callState',
    'callType',
    'canControlLayout',
    'canDial',
    'canEndMeeting',
    'canInvite',
    'canRecordMeeting',
    'canSpotlight',
    'contactName',
    'defaultCallInCountryId',
    'dialPrefix',
    'dialString',
    'direction',
    'displayDialString',
    'end',
    'extraDialString',
    'isBeingRecorded',
    'layouts',
    'meetingCodes',
    'meetingPassword',
    'missed',
    'participantId',
    'receiveGain',
    'sendingContent',
    'sendingVideo',
    'start',
];
const ignoreProps = ['callId', 'companyId', 'roomId', 'siteId'];
class DeferredCallUpdate extends DeferredMerge {
    constructor(callId, getRoom, timeoutMs, timeouts) {
        super(timeoutMs, timeouts);
        this.callId = callId;
        this.getRoom = getRoom;
    }
    doMerge(update, state) {
        return __awaiter(this, void 0, void 0, function* () {
            if (state.cancelled) {
                log.info(`properties update cancelled for callId "${this.callId}"`);
                return;
            }
            const room = this.getRoom();
            if (room === undefined)
                return;
            log.info(`updating properties for callId "${this.callId}"`);
            const calls = room.$getCalls(defaults.inara.numRecentCalls);
            yield calls.$loaded();
            const call = calls.$recordAt(this.callId);
            if (!call) {
                log.error(`no call with id "${this.callId}"`);
                return;
            }
            const values = call.$getValues();
            for (const prop of callProps) {
                if (update[prop] === undefined) {
                    delete values[prop];
                }
                else {
                    values[prop] = update[prop];
                }
            }
            yield call.$set(values);
            log.info(`properties update for callId "${this.callId}" complete`);
        });
    }
    merge(value, currValue) {
        if (currValue === undefined)
            return value;
        const props = [...Object.keys(currValue !== null && currValue !== void 0 ? currValue : {}), ...Object.keys(value)].filter((v, i, a) => a.indexOf(v) === i);
        for (const prop of props) {
            if (callProps.includes(prop)) {
                if (value[prop] === undefined) {
                    delete currValue[prop];
                }
                else {
                    currValue[prop] = value[prop];
                }
            }
            else if (!ignoreProps.includes(prop)) {
                log.error(`unhandled call property: ${prop}`);
            }
        }
        return currValue;
    }
}
class DeferredParticipantsUpdate extends DeferredMerge {
    constructor(callId, getRoom, timeoutMs, timeouts) {
        super(timeoutMs, timeouts);
        this.callId = callId;
        this.getRoom = getRoom;
    }
    doMerge(value, state) {
        return __awaiter(this, void 0, void 0, function* () {
            if (state.cancelled) {
                log.info(`participants update cancelled for callId "${this.callId}"`);
                return;
            }
            const start = Date.now();
            const room = this.getRoom();
            if (room === undefined)
                return;
            log.info(`updating participants for callId "${this.callId}"`);
            const calls = yield room.$getCalls(defaults.inara.numRecentCalls).$loaded();
            const call = calls.$recordAt(this.callId);
            if (!call) {
                log.error(`no call with id "${this.callId}"`);
                return;
            }
            if (call.callState === 'disconnected' && value !== 'delete') {
                log.info(`aborting participants update for callId "${this.callId}", call disconnected`);
                return;
            }
            yield call.$getParticipants().$setAll(value === 'delete' ? null : value);
            const elapsedSec = (0.001 * (Date.now() - start)).toFixed(1);
            const elapsedStr = `elapsed time: ${elapsedSec} seconds`;
            log.info(`participants update for callId "${this.callId}" complete, ${elapsedStr}`);
        });
    }
    merge(value, currValue) {
        return value;
    }
}
export class CallUpdateManager {
    constructor() {
        this.participantsUpdates = {};
        this.callUpdates = {};
        this.timeouts = new Timeouts();
    }
    disable() {
        log.debug(`disabling`);
        this.room = undefined;
        for (const update of Object.values(this.participantsUpdates)) {
            update.cancel();
        }
        this.participantsUpdates = {};
        for (const update of Object.values(this.callUpdates)) {
            update.cancel();
        }
        this.callUpdates = {};
    }
    enable(room) {
        log.debug(`enabling`);
        this.room = room;
    }
    updateCall(callId, call) {
        return __awaiter(this, void 0, void 0, function* () {
            let callUpdate = this.callUpdates[callId];
            if (callUpdate === undefined) {
                callUpdate = new DeferredCallUpdate(callId, () => this.room, 500, this.timeouts);
                callUpdate.onComplete(() => {
                    callUpdate.destroy();
                    delete this.callUpdates[callId];
                });
                this.callUpdates[callId] = callUpdate;
            }
            callUpdate.update(Object.assign({}, call));
        });
    }
    updateParticipant(callId, participantId, participant) {
        return __awaiter(this, void 0, void 0, function* () {
            const participantsUpdate = this.participantsUpdates[callId];
            // If there's no full participants update pending, then just write the participant directly
            // to the database. If there is a full participants update pending, then add this
            // participant to that update.
            if (participantsUpdate === undefined) {
                if (this.room === undefined)
                    return;
                log.info(`updating participant ${participantId} for callId ${callId}`);
                const calls = yield this.room.$getCalls(defaults.inara.numRecentCalls).$loaded();
                const call = calls.$recordAt(callId);
                if (!call) {
                    log.error(`no call with id "${callId}"`);
                    return;
                }
                if (call.callState === 'disconnected') {
                    log.info(`aborting participant update for callId "${callId}", call disconnected`);
                    return;
                }
                yield call.$getParticipants().$set(participantId, participant !== null && participant !== void 0 ? participant : null);
            }
            else {
                if (participantsUpdate.value === undefined) {
                    if (this.room === undefined)
                        return;
                    const calls = yield this.room.$getCalls(defaults.inara.numRecentCalls).$loaded();
                    const call = calls.$recordAt(callId);
                    if (!call)
                        return;
                    yield call.$getParticipants().$set(participantId, participant !== null && participant !== void 0 ? participant : null);
                }
                else {
                    if (participant === undefined) {
                        if (participantsUpdate.value !== 'delete') {
                            delete participantsUpdate.value[participantId];
                        }
                    }
                    else {
                        if (participantsUpdate.value === 'delete') {
                            participantsUpdate.cancel();
                            participantsUpdate.update({ [participantId]: participant });
                        }
                        else {
                            participantsUpdate.value[participantId] = participant;
                        }
                    }
                }
            }
        });
    }
    updateParticipants(callId, participants) {
        return __awaiter(this, void 0, void 0, function* () {
            let participantsUpdate = this.participantsUpdates[callId];
            if (participantsUpdate === undefined) {
                participantsUpdate = new DeferredParticipantsUpdate(callId, () => this.room, 1000, this.timeouts);
                participantsUpdate.onComplete(() => {
                    participantsUpdate.destroy();
                    delete this.participantsUpdates[callId];
                });
                this.participantsUpdates[callId] = participantsUpdate;
            }
            participantsUpdate.update(participants !== null && participants !== void 0 ? participants : 'delete');
        });
    }
}
